<template>
  <div>
    <div v-if="!showSearch" class="level is-mobile">
      <div class="level-left">
        <div class="level-item">
          <b-icon icon="link-variant" class="mr-2" />
          <span
            >Legat de intrarea nr. <strong>{{ linkedLabel }}</strong></span
          >
        </div>
      </div>
      <div v-if="!disabled" class="level-right">
        <div class="level-item buttons">
          <b-button size="is-small" @click="changing = true">Schimba</b-button>
          <b-button size="is-small" type="is-danger" outlined @click="clear">
            Sterge legatura
          </b-button>
        </div>
      </div>
    </div>
    <template v-else>
      <div class="columns">
        <div class="column is-5">
          <b-field label="Registru">
            <b-select v-model="registryId" expanded :disabled="disabled">
              <option
                v-for="registry in registries"
                :key="registry.id"
                :value="registry.id"
              >
                {{ registry.name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-2">
          <b-field label="An">
            <b-input v-model="year" type="number" :disabled="disabled" />
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Numar" :type="notFound ? 'is-danger' : ''">
            <b-input
              v-model="number"
              type="number"
              :disabled="disabled"
              @keyup.native.enter="search"
            />
          </b-field>
        </div>
        <!-- Butonul se aliniaza cu campurile, fara eticheta proprie. -->
        <div class="column is-2 lookup-action">
          <b-field>
            <b-button
              expanded
              icon-left="magnify"
              :loading="searching"
              :disabled="disabled"
              @click="search"
            >
              Cauta
            </b-button>
          </b-field>
        </div>
      </div>
      <p v-if="notFound" class="help is-danger">{{ notFoundMessage }}</p>
      <div v-if="found" class="notification is-success is-light">
        {{ foundText }}
        <b-button size="is-small" type="is-success" class="ml-2" @click="link"
          >Leaga</b-button
        >
      </div>
      <b-button v-if="changing" size="is-small" @click="changing = false">
        Renunta la schimbare
      </b-button>
    </template>
  </div>
</template>

<script>
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import { describeError } from "@/helpers/resdpsErrors";
import { formatDate } from "@/helpers/resdps";

const NOT_FOUND_MESSAGE = "Intrarea nu exista in registratura generala.";

// Legatura cu intrarea din registratura generala (spec E, decizia E2): cautare exacta dupa registru, an si numar.
export default {
  name: "RegistryEntryLookup",
  props: {
    value: {
      type: Number,
      default: null,
    },
    linkedNumber: {
      type: [Number, String],
      default: null,
    },
    linkedDate: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      registries: [],
      registryId: null,
      year: String(new Date().getFullYear()),
      number: "",
      searching: false,
      found: null,
      notFound: false,
      changing: false,
      linked: null,
      notFoundMessage: NOT_FOUND_MESSAGE,
    };
  },
  computed: {
    hasLink() {
      return this.value !== null && this.value !== undefined;
    },
    showSearch() {
      return !this.hasLink || this.changing;
    },
    linkedLabel() {
      const source = this.linked || {
        number: this.linkedNumber,
        date: this.linkedDate,
      };
      return source.number
        ? source.number + " din " + formatDate(source.date)
        : "#" + this.value;
    },
    foundText() {
      const entry = this.found;
      return (
        "Nr. " +
        entry.number +
        " din " +
        formatDate(entry.date) +
        " · " +
        (entry.type || "—") +
        " · emitent: " +
        (entry.issuer || "—") +
        " · " +
        (entry.description || "")
      );
    },
  },
  watch: {
    value(value) {
      if (value === null) this.linked = null;
    },
  },
  created() {
    this.loadRegistries();
  },
  methods: {
    // Lista din API tine cont doar de prima permisiune a utilizatorului (limitare existenta, spec E, riscul 7).
    async loadRegistries() {
      try {
        const result = await SuccessionService.listRegistries();
        this.registries = Array.isArray(result) ? result : result.items || [];
        if (this.registryId === null && this.registries.length > 0) {
          this.registryId = this.registries[0].id;
        }
      } catch (error) {
        notify.error(this, describeError(error).message);
      }
    },
    async search() {
      const year = parseInt(this.year, 10);
      const number = parseInt(this.number, 10);
      if (!this.registryId || !year || !number) {
        notify.error(this, "Alegeti registrul si completati anul si numarul.");
        return;
      }
      this.searching = true;
      this.found = null;
      this.notFound = false;
      try {
        this.found = await SuccessionService.findGeneralRegistryEntry(
          this.registryId,
          year,
          number
        );
      } catch (error) {
        const described = describeError(error);
        if (described.status === 404) this.notFound = true;
        else notify.error(this, described.message);
      } finally {
        this.searching = false;
      }
    },
    link() {
      this.linked = { number: this.found.number, date: this.found.date };
      this.$emit("input", this.found.id);
      this.$emit("linked", this.found);
      this.found = null;
      this.changing = false;
    },
    clear() {
      this.linked = null;
      this.changing = false;
      this.$emit("input", null);
      this.$emit("linked", null);
    },
  },
};
</script>

<style scoped>
/* Butonul de cautare sta pe linia campurilor, nu la mijlocul lor, ca sa nu aiba nevoie de o eticheta goala. */
.lookup-action {
  align-self: flex-end;
}
</style>
